import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { MediaUploadResult } from '@core/media/models/media-upload.models';
import { MediaUploadService } from '@core/media/services/media-upload.service';
import { mediaUploadConfig } from '@core/media/config/media-upload.config';
import { ProductGalleryImage } from '@features/catalog/models/product.models';
import { mediaUrlForContext } from '@core/media/utils/media-image.util';

export const PRODUCT_FORM_IMAGE_MAX_MB = mediaUploadConfig.maxMegabytes;

export type ProductFormImageValidationError =
  | { kind: 'invalid-type'; messageKey: 'catalog.products.invalidImageType' }
  | { kind: 'too-large'; messageKey: 'catalog.products.imageTooLarge'; size: number }
  | { kind: 'no-tenant'; messageKey: 'catalog.products.loadError' };

export interface ProductFormMediaUploadContext {
  companyId: number | string | null;
  productId: string | null;
}

@Injectable()
export class ProductFormMediaService {
  private readonly mediaUpload = inject(MediaUploadService);

  /**
   * Solo valida el tipo aqui. El tamano se valida en `MediaUploadService.uploadImage`
   * despues de comprimir: el archivo crudo (recien recortado/zoomeado, potencialmente
   * PNG sin comprimir) puede pesar mas que el limite y aun asi entrar una vez comprimido.
   */
  validateImageFile(file: File): ProductFormImageValidationError | null {
    if (!file.type.startsWith('image/')) {
      return { kind: 'invalid-type', messageKey: 'catalog.products.invalidImageType' };
    }
    return null;
  }

  resolvePreviewUrl(result: MediaUploadResult): string {
    return mediaUrlForContext(result.urls, 'form-preview', result.deliveryUrl) ?? result.deliveryUrl;
  }

  toGalleryImage(result: MediaUploadResult, position: number): ProductGalleryImage | null {
    if (!result.mediaAssetId) {
      return null;
    }
    return {
      id: result.mediaAssetId,
      deliveryUrl: result.deliveryUrl,
      position,
      urls: result.urls,
    };
  }

  uploadProductMainImage(file: File, ctx: ProductFormMediaUploadContext): Observable<MediaUploadResult> {
    return this.mediaUpload.uploadImage(file, {
      kind: 'product_main',
      companyId: String(ctx.companyId),
      productId: ctx.productId!,
    });
  }

  uploadGalleryImage(file: File, ctx: ProductFormMediaUploadContext): Observable<MediaUploadResult> {
    return this.mediaUpload.uploadImage(file, {
      kind: 'product_gallery',
      companyId: String(ctx.companyId),
      productId: ctx.productId!,
    });
  }

  uploadVariantImage(
    file: File,
    ctx: ProductFormMediaUploadContext & { variantId: string }
  ): Observable<MediaUploadResult> {
    return this.mediaUpload.uploadImage(file, {
      kind: 'product_variant',
      companyId: String(ctx.companyId),
      productId: ctx.productId!,
      variantId: ctx.variantId,
    });
  }

  uploadModifierOptionImage(
    file: File,
    ctx: ProductFormMediaUploadContext & { modifierOptionId: string }
  ): Observable<MediaUploadResult> {
    return this.mediaUpload.uploadImage(file, {
      kind: 'product_modifier_option',
      companyId: String(ctx.companyId),
      productId: ctx.productId!,
      modifierOptionId: ctx.modifierOptionId,
    });
  }

  archiveGalleryImage(assetId: string): Observable<void> {
    return this.mediaUpload.archiveMediaAsset(assetId);
  }

  canUploadToServer(ctx: ProductFormMediaUploadContext): boolean {
    return Boolean(ctx.companyId && ctx.productId);
  }

  missingTenantError(): ProductFormImageValidationError {
    return { kind: 'no-tenant', messageKey: 'catalog.products.loadError' };
  }
}
